import { useCallback, useEffect, useState, useRef } from "react";

interface Message {
  _type: string;
  [key: string]: any;
}

export interface WebSocketHandle {
  send: (data: any) => void;
  socket?: WebSocket;
  open: boolean;
}

type UseWebsocket = (
  getSocket: () => Promise<WebSocket>,
  onMessage: (message: Message) => void,
  dontConnect?: boolean
) => WebSocketHandle;

export const useSaferWebsocket: UseWebsocket = (getSocket, onMessage, dontConnect) => {
  const [socket, setSocket] = useState<WebSocket>();
  const [open, setOpen] = useState(false);

  // 1. Keep the latest onMessage in a ref to avoid stale closures
  // without needing to re-bind the socket event on every render.
  const onMessageRef = useRef(onMessage);
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  // Keep getSocket in a ref so we don't need it in the dependency array
  const getSocketRef = useRef(getSocket);
  useEffect(() => {
    getSocketRef.current = getSocket;
  }, [getSocket]);

  useEffect(() => {
    if (dontConnect) return;

    let isMounted = true;
    let currentSocket: WebSocket | null = null;
    let reconnectTimeoutId: ReturnType<typeof setTimeout>;

    const connect = () => {
      getSocketRef.current()
        .then((ws) => {
          // FIX: If the component unmounted while we were fetching the token, 
          // instantly close the newly created socket and exit.
          if (!isMounted) {
            ws.close();
            return;
          }

          currentSocket = ws;
          setSocket(ws);

          ws.onopen = () => {
            if (isMounted) setOpen(true);
          };

          ws.onmessage = (evt) => {
            if (isMounted) {
              try {
                onMessageRef.current(JSON.parse(evt.data));
              } catch (e) {
                console.error("Failed to parse websocket message", e);
              }
            }
          };

          ws.onclose = () => {
            if (isMounted) {
              setOpen(false);
              setSocket(undefined);
              // RECONNECT LOGIC: Try to reconnect after 3 seconds
              reconnectTimeoutId = setTimeout(connect, 3000);
            }
          };

          // Note: ws.onerror doesn't need to trigger reconnect directly
          // because onclose will always fire immediately after onerror.
        })
        .catch((error) => {
          console.error("Failed to establish WebSocket connection", error);
          // If token fetch fails, wait and try again
          if (isMounted) {
            reconnectTimeoutId = setTimeout(connect, 5000);
          }
        });
    };

    connect();

    // CLEANUP
    return () => {
      isMounted = false;
      clearTimeout(reconnectTimeoutId);
      if (currentSocket) {
        // Nullify onclose so our intentional closing doesn't trigger a reconnect
        currentSocket.onclose = null; 
        currentSocket.close();
      }
    };
  }, [dontConnect]); 

  const send = useCallback((data: any) => {
    // Rely on the actual socket readyState rather than React state
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(data));
      return true;
    } else {
      console.warn("WebSocket is not connected");
      return false;
    }
  }, [socket]);

  return { send, socket, open };
};
